import React, { useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Link,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ShortAbout from "../images/htruck.jpg";
import ShortContact from "../images/container.jpg";
import backgroundVideo from "../videos/video2Q.mp4";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

const MotionTypography = motion(Typography);
const MotionButton = motion(Button);

const Home = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);

  // Force autoplay on mobile
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    }
  }, []);

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          height: "100vh",
          position: "relative",
          overflow: "hidden",
          color: "white",
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Background video */}
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          webkit-playsinline="true"
          controls={false}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: -2,
          }}
        >
          <source src={backgroundVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Dark overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(14, 43, 74, 0.75)",
            zIndex: -1,
          }}
        />

        {/* Hero Content */}
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <MotionTypography
                variant="h6"
                sx={{
                  color: "#ff5b1f",
                  mb: 2,
                  fontSize: { xs: "1rem", sm: "1.2rem", md: "1.3rem" },
                }}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                Welcome to Mangalmurti Transport
              </MotionTypography>

              <MotionTypography
                variant="h3"
                fontWeight="bold"
                gutterBottom
                sx={{
                  fontSize: { xs: "1.8rem", sm: "2.4rem", md: "3rem" },
                  lineHeight: 1.2,
                }}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1 }}
              >
                They say it can’t be done,
                <br />
                we say it <span style={{ color: "#ff5b1f" }}>WILL</span> be done
              </MotionTypography>

              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                  lineHeight: 1.5,
                }}
              >
                We’re dedicated to getting your freight delivered on time, every
                time.
                <br /> With decades of experience in logistics and fleet
                management,
                <br />
                Mangalmurti Transport is the partner you can count on.
              </Typography>

              <MotionButton
                onClick={() => navigate("/contact")}
                variant="contained"
                sx={{
                  backgroundColor: "#ff5b1f",
                  fontWeight: 600,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1, sm: 1.5 },
                  borderRadius: 3,
                  "&:hover": { backgroundColor: "#ff5b1f" },
                }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              >
                Contact Us Today
              </MotionButton>
            </Grid>
          </Grid>
        </Container>

        {/* Decorative bottom strip */}
        <Box
          sx={{
            height: "10px",
            background: "#ff5b1f",
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            transform: "skewY(-2deg)",
          }}
        />
      </Box>

      {/* Short About Section */}
      <Box
        sx={{ py: 10, background: "linear-gradient(135deg, #f1f8e9, #e0f2f1)" }}
      >
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.3 } },
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: { xs: 5, md: 8 },
                alignItems: "center",
              }}
            >
              {/* LEFT: Text Content */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, x: -80 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    transition: { duration: 0.8, ease: "easeOut" },
                  },
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  sx={{
                    mb: 3,
                    color: "#e65100",
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  About Mangalmurti Transport
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.8,
                    mb: 3,
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  At Mangalmurti Transport, we pride ourselves on delivering
                  goods safely and on time. With decades of logistics expertise
                  and a customer-first mindset, we offer services you can rely
                  on for all your transport needs.
                </Typography>

                {/* Button BELOW text */}
                <Box textAlign={{ xs: "center", md: "left" }}>
                  <Button
                    component={Link}
                    onClick={() => navigate("/about")}
                    variant="contained"
                    sx={{
                      backgroundColor: "#f4511e",
                      "&:hover": { backgroundColor: "#e64a19" },
                      borderRadius: "6px",
                      px: 3,
                      py: 1,
                      fontWeight: "bold",
                    }}
                  >
                    Know More
                  </Button>
                </Box>
              </motion.div>

              {/* RIGHT: Image */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, x: 80 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    transition: { duration: 0.8, ease: "easeOut" },
                  },
                }}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 120 }}
                  style={{
                    position: "relative",
                    borderRadius: "20px",
                    overflow: "hidden",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                    minHeight: 360,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={ShortAbout}
                    alt="MangalMurti Truck"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </motion.div>
              </motion.div>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 8, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            fontWeight="bold"
            gutterBottom
            sx={{ textAlign: "center", color: "#0e2b4a" }}
          >
            Our Services
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{ textAlign: "center", color: "text.secondary", mb: 6 }}
          >
            Providing reliable, efficient, and timely transport services for all
            your logistics needs.
          </Typography>

          <Box
            sx={{
              display: "flex",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              gap: 3,
              pb: 2,
              maxWidth: "100%",
              width: "100%",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {/* Card 1 */}
            <Box
              sx={{
                minWidth: { xs: "280px", sm: "300px", md: "320px" },
                maxWidth: { xs: "85vw", sm: "45vw", md: "28vw" },
                scrollSnapAlign: "start",
                textAlign: "center",
                p: 3,
                borderRadius: 3,
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                flexShrink: 0,
                transition: "all 0.3s ease",
                "&:hover": { transform: "translateY(-6px)" },
              }}
            >
              <LocalShippingIcon
                sx={{ fontSize: 48, color: "#f4511e", mb: 2 }}
              />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Reliable Transport
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fast and secure delivery across all destinations.
              </Typography>
            </Box>

            {/* Card 2 */}
            <Box
              sx={{
                minWidth: { xs: "280px", sm: "300px", md: "320px" },
                maxWidth: { xs: "85vw", sm: "45vw", md: "28vw" },
                scrollSnapAlign: "start",
                textAlign: "center",
                p: 4,
                borderRadius: 3,
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                flexShrink: 0,
                transition: "all 0.3s ease",
                "&:hover": { transform: "translateY(-6px)" },
              }}
            >
              <AcUnitIcon sx={{ fontSize: 48, color: "#ff7043", mb: 2 }} />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Cold Storage
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Maintain freshness with our refrigerated trucks.
              </Typography>
            </Box>

            {/* Card 3 */}
            <Box
              sx={{
                minWidth: { xs: "280px", sm: "300px", md: "320px" },
                maxWidth: { xs: "85vw", sm: "45vw", md: "28vw" },
                scrollSnapAlign: "start",
                textAlign: "center",
                p: 4,
                borderRadius: 3,
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                flexShrink: 0,
                transition: "all 0.3s ease",
                "&:hover": { transform: "translateY(-6px)" },
              }}
            >
              <FlashOnIcon sx={{ fontSize: 48, color: "#ff5722", mb: 2 }} />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Express Delivery
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Urgent shipments delivered on time, every time.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ textAlign: "center", mt: 6 }}>
            <Button
              variant="contained"
              onClick={() => navigate("/services")}
              sx={{
                backgroundColor: "#f4511e",
                "&:hover": { backgroundColor: "#e64a19" },
                px: 4,
                py: 1.5,
                borderRadius: "30px",
                fontWeight: "bold",
              }}
            >
              View All Services
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        sx={{
          position: "relative",
          backgroundImage: `url(${ShortContact})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          py: { xs: 4, sm: 5, md: 8 },
          px: 0,
          my: 4,
          color: "#fff",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.65)",
            backdropFilter: "blur(3px)",
            zIndex: 0,
          },
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            px: { xs: 2, sm: 3, md: 8 },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Grid
            container
            alignItems="center"
            spacing={3}
            sx={{
              flexDirection: { xs: "column", md: "row" },
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Grid item>
              <LocalPhoneIcon
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3.2rem" },
                  color: "#ff5b1f",
                }}
              />
            </Grid>
            <Grid item xs>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
                  lineHeight: 1.3,
                }}
              >
                Looking for the best transport services?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mt: 1,
                  fontSize: { xs: "0.9rem", sm: "1rem", md: "1.05rem" },
                  lineHeight: 1.5,
                }}
              >
                We make your cargo transport simple. Contact us today.
              </Typography>
            </Grid>
            <Grid
              item
              sx={{
                ml: { md: "auto" },
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", md: "flex-end" },
                mt: { xs: 2, md: 0 },
              }}
            >
              <Button
                variant="contained"
                onClick={() => navigate("/contact")}
                sx={{
                  backgroundColor: "#ff5b1f",
                  fontWeight: "bold",
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.2, sm: 1.5 },
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  borderRadius: 3,
                  boxShadow: "0 4px 15px rgba(255, 91, 31, 0.6)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#e64a14",
                    boxShadow: "0 6px 20px rgba(230, 74, 20, 0.8)",
                    transform: "scale(1.05)",
                  },
                }}
              >
                Contact Us Today
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Floating WhatsApp Button */}
      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 15, sm: 20 },
          right: { xs: 15, sm: 20 },
          zIndex: 2000,
        }}
      >
        <Button
          href="https://wa.me/919987174246?text=Hi%2C%20I%27m%20interested%20in%20your%20transport%20services"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            backgroundColor: "#25D366",
            color: "white",
            minWidth: { xs: "50px", sm: "60px" },
            height: { xs: "50px", sm: "60px" },
            borderRadius: "50%",
            boxShadow: "0px 4px 15px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": {
              backgroundColor: "#128C7E",
              transform: "scale(1.1)",
            },
          }}
        >
          <WhatsAppIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
        </Button>
      </Box>
    </>
  );
};

export default Home;
